/**
 * Say something new in the model, either through a wizard or as SysML.
 *
 * ONE ENTRY with two tabs (issue #53, Patrick 2026-09-28), replacing the tree's
 * separate "Add a declaration inside" and "Paste SysML inside". Both are the
 * same question - what goes in this package - asked two ways:
 *
 *   - WIZARD: choose a kind, and get THAT kind's own wizard - the Part wizard,
 *     the requirement dialog, the state wizard, or the generic create form -
 *     with the package already filled in. The tree used to have its own
 *     generic form, which was a second, thinner way to make a part than the
 *     Parts screen offers, and it drifted: a requirement made there never got
 *     its sources asked for.
 *   - SYSML: the paste, previewed then applied.
 *
 * Opened on a package row, the package is fixed. Opened from the toolbar there
 * is none yet: the wizards bring their own pickers, and the SysML tab asks.
 *
 * Where it lands is SHOWN. A split package (issue #27) is declared in several
 * files, and every create path writes to its home declaration - the file named
 * for it - so the dialog says which file that is before anything is written.
 */

import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { ApiError, api } from '../../api'
import type { KindSchema, PackageRow, Schema } from '../../schema'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import CreateEntityDialog from '../entity/CreateEntityDialog'
import CreateRequirementDialog from '../entity/CreateRequirementDialog'
import CreateVerificationDialog from '../entity/CreateVerificationDialog'
import PartWizard from '../parts/PartWizard'
import CreateStateDialog from '../states/CreateStateDialog'
import PasteSysmlPanel from './PasteSysmlPanel'

/** The pseudo-kind for "another package", which has its own create route. */
const PACKAGE = '__package__'

type Mode = 'wizard' | 'sysml'

export default function NewElementDialog({
  schema,
  pkg = '',
  onClose,
  onCreated,
}: {
  schema: Schema
  /** Qualified name of the package it goes in, or '' from the toolbar. */
  pkg?: string
  onClose: () => void
  /**
   * After a write. With a message to show, or without one when the wizard has
   * already said so itself - the Part wizard, the requirement and entity
   * dialogs and the paste all toast their own success.
   */
  onCreated: (message?: string) => void
}) {
  const creatable = schema.kinds.filter((entry) => entry.can_create)

  const [mode, setMode] = useState<Mode>('wizard')
  const [kindName, setKindName] = useState(creatable[0]?.name ?? PACKAGE)
  /** The kind whose wizard is open, once somebody has chosen to go on. */
  const [open, setOpen] = useState<KindSchema | typeof PACKAGE | null>(null)
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [chosenPackage, setChosenPackage] = useState(pkg)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    api
      .packages()
      .then((items) => live && setPackages(items))
      .catch(() => live && setPackages([]))
    return () => {
      live = false
    }
  }, [])

  const destination = pkg || chosenPackage
  const target = packages.find((row) => row.qualified_name === destination)?.path

  const back = () => setOpen(null)
  const quietly = () => onCreated()

  // Each wizard is its own dialog, so choosing one REPLACES this one; cancelling
  // it comes back here to choose something else.
  if (open === PACKAGE) {
    return <NewPackageDialog parent={pkg} packages={packages} onClose={back} onCreated={onCreated} />
  }
  if (open?.name === 'part') {
    return <PartWizard kind={open} pkg={pkg} onClose={back} onCreated={quietly} />
  }
  if (open?.name === 'requirement') {
    return <CreateRequirementDialog kind={open} pkg={pkg} onClose={back} onCreated={quietly} />
  }
  if (open?.name === 'verification') {
    return (
      <CreateVerificationDialog
        kind={open}
        pkg={pkg}
        onClose={back}
        onCreated={(identifier) => onCreated(`${identifier} created`)}
      />
    )
  }
  if (open?.name === 'state') {
    return (
      <CreateStateDialog
        inside={pkg ? { qualifiedName: pkg, label: pkg } : undefined}
        onClose={back}
        onCreated={(identifier) => onCreated(`${identifier} created`)}
      />
    )
  }
  if (open) {
    return (
      <CreateEntityDialog
        kind={open}
        owner={pkg ? { qualifiedName: pkg, label: pkg } : undefined}
        onClose={back}
        onCreated={quietly}
      />
    )
  }

  const proceed = () => {
    if (kindName === PACKAGE) setOpen(PACKAGE)
    else setOpen(creatable.find((entry) => entry.name === kindName) ?? null)
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        New
        {pkg && (
          <Typography variant="caption" color="text.secondary" component="div">
            In <span style={{ fontFamily: MONOSPACE }}>{pkg}</span>
            {target && (
              <>
                {' '}· written to <span style={{ fontFamily: MONOSPACE }}>{target}</span>
              </>
            )}
          </Typography>
        )}
      </DialogTitle>
      <Tabs value={mode} onChange={(_event, next: Mode) => setMode(next)} sx={{ px: 3 }}>
        <Tab value="wizard" label="Wizard" disabled={busy} />
        <Tab value="sysml" label="SysML" disabled={busy} />
      </Tabs>

      {mode === 'wizard' ? (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Each kind opens its own wizard, the same one its screen uses
                {pkg ? ', starting in this package' : ''}.
              </Typography>
              <FormControl size="small" required>
                <InputLabel id="tree-new-kind">Kind</InputLabel>
                <Select
                  labelId="tree-new-kind"
                  label="Kind"
                  value={kindName}
                  onChange={(event) => setKindName(event.target.value)}
                >
                  <MenuItem value={PACKAGE}>Package</MenuItem>
                  {creatable.map((entry) => (
                    <MenuItem key={entry.name} value={entry.name}>
                      {entry.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={proceed}>
              Continue
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          {!pkg && (
            <DialogContent sx={{ pb: 0 }}>
              <Stack spacing={1} sx={{ pt: 1 }}>
                <ElementPicker
                  options={packages.map((entry) => ({ value: entry.qualified_name }))}
                  value={chosenPackage || null}
                  onChange={setChosenPackage}
                  label="Package"
                  required
                  monospace
                  helperText="Pasted SysML goes into a package"
                />
                {target && (
                  <Typography variant="caption" color="text.secondary">
                    Written to <span style={{ fontFamily: MONOSPACE }}>{target}</span>
                  </Typography>
                )}
              </Stack>
            </DialogContent>
          )}
          <PasteSysmlPanel
            pkg={destination}
            onBusy={setBusy}
            onClose={onClose}
            onPasted={quietly}
          />
        </>
      )}
    </Dialog>
  )
}

/**
 * A new package, inside the one the tree chose or one picked here.
 *
 * No wizard of its own anywhere else: a package is a name and a place, so this
 * is the whole of it. A root package is not offered - BEST places a package
 * beneath an existing one until models are owned by a project.
 */
function NewPackageDialog({
  parent: fixed,
  packages,
  onClose,
  onCreated,
}: {
  parent: string
  packages: PackageRow[]
  onClose: () => void
  onCreated: (message: string) => void
}) {
  const [parent, setParent] = useState(fixed)
  const [shortName, setShortName] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      const created = await api.createPackage({
        parent,
        name: name || shortName,
        short_name: shortName || undefined,
      })
      onCreated(`${created.qualified_name} created`)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        New package
        {fixed && (
          <Typography variant="caption" color="text.secondary" component="div">
            In <span style={{ fontFamily: MONOSPACE }}>{fixed}</span>
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          {!fixed && (
            <ElementPicker
              options={packages.map((entry) => ({ value: entry.qualified_name }))}
              value={parent || null}
              onChange={setParent}
              label="Parent package"
              required
              monospace
              error={Boolean(fieldErrors.parent)}
              helperText={fieldErrors.parent || 'Where the new package goes'}
            />
          )}
          <TextField
            label="Short name"
            required
            value={shortName}
            onChange={(event) => setShortName(event.target.value)}
            error={Boolean(fieldErrors.short_name)}
            helperText={fieldErrors.short_name || 'The short name, in <angle brackets>'}
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'The declared name; defaults to the short name'}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy || !shortName || !parent}
        >
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
